import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2, Search, Eye, EyeOff, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { db } from '@/lib/db';
import type { Property } from '@/types';

export default function ListingsPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState<string>('all');
  const [propertyStatusFilter, setPropertyStatusFilter] = useState<string>('all');
  const [propertyCategoryFilter, setPropertyCategoryFilter] = useState<string>('all');
  const [propertyCityFilter, setPropertyCityFilter] = useState<string>('all');
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [updatingVisibility, setUpdatingVisibility] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchProperties();
  }, [user?.id, searchQuery, propertyTypeFilter, propertyStatusFilter, propertyCategoryFilter, propertyCityFilter]);

  const fetchProperties = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Build the query with filters
      let query = supabase
        .from('properties')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Apply search filter
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
      }

      // Apply type filter
      if (propertyTypeFilter !== 'all') {
        query = query.eq('type', propertyTypeFilter);
      }

      // Apply status filter
      if (propertyStatusFilter !== 'all') {
        query = query.eq('status', propertyStatusFilter);
      }

      // Apply category filter
      if (propertyCategoryFilter !== 'all') {
        query = query.eq('category', propertyCategoryFilter);
      }

      // Apply city filter
      if (propertyCityFilter !== 'all') {
        query = query.eq('city', propertyCityFilter);
      }

      const { data, error } = await db.fetch(query);

      if (error) throw error;
      setProperties(data || []);

      // Extract unique cities for the city filter
      if (data) {
        const cities = [...new Set(data.map(property => property.city).filter(Boolean))].sort();
        setAvailableCities(cities);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Erro ao carregar imóveis. Tentando novamente...');
      
      // Retry after a delay
      setTimeout(fetchProperties, 2000);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (e: React.MouseEvent, propertyId: string, currentVisibility: boolean) => {
    // Prevent card click when toggling visibility
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setUpdatingVisibility(propertyId);
      
      const { error } = await supabase
        .from('properties')
        .update({ is_visible_on_storefront: !currentVisibility })
        .eq('id', propertyId);

      if (error) throw error;

      // Update local state
      setProperties(prev => 
        prev.map(property => 
          property.id === propertyId 
            ? { ...property, is_visible_on_storefront: !currentVisibility }
            : property
        )
      );

      toast.success(
        !currentVisibility 
          ? 'Imóvel agora está visível na vitrine' 
          : 'Imóvel foi ocultado da vitrine'
      );
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Erro ao atualizar visibilidade');
    } finally {
      setUpdatingVisibility(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'disponivel':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Disponível</Badge>;
      case 'vendido':
        return <Badge variant="destructive">Vendido</Badge>;
      case 'alugado':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Alugado</Badge>;
      default:
        return null;
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setPropertyTypeFilter('all');
    setPropertyStatusFilter('all');
    setPropertyCategoryFilter('all');
    setPropertyCityFilter('all');
  };

  const hasActiveFilters = searchQuery || propertyTypeFilter !== 'all' || propertyStatusFilter !== 'all' || 
                          propertyCategoryFilter !== 'all' || propertyCityFilter !== 'all';

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold">Meus Imóveis</h1>
          <Button disabled>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Carregando...
          </Button>
        </div>

        {/* Loading skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <Card key={n} className="animate-pulse">
              <CardContent className="p-4">
                <div className="aspect-video bg-muted rounded-lg mb-4" />
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2 mb-4" />
                <div className="h-8 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">Meus Imóveis</h1>
        <Link to="/dashboard/listings/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Novo Imóvel
          </Button>
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4 mb-8">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, cidade ou endereço..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="h-4 w-4" />
            Filtros:
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            {/* Type Filter */}
            <Select value={propertyTypeFilter} onValueChange={setPropertyTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="venda">Venda</SelectItem>
                <SelectItem value="aluguel">Aluguel</SelectItem>
                <SelectItem value="venda_aluguel">Venda e Aluguel</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={propertyStatusFilter} onValueChange={setPropertyStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="disponivel">Disponível</SelectItem>
                <SelectItem value="vendido">Vendido</SelectItem>
                <SelectItem value="alugado">Alugado</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={propertyCategoryFilter} onValueChange={setPropertyCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                <SelectItem value="Lançamento">Lançamento</SelectItem>
                <SelectItem value="Na planta">Na planta</SelectItem>
                <SelectItem value="Em Construção">Em Construção</SelectItem>
                <SelectItem value="Pronto para morar">Pronto para morar</SelectItem>
              </SelectContent>
            </Select>

            {/* City Filter */}
            <Select value={propertyCityFilter} onValueChange={setPropertyCityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Cidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as cidades</SelectItem>
                {availableCities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearAllFilters} className="shrink-0">
              Limpar Filtros
            </Button>
          )}
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="text-sm text-muted-foreground">
            {properties.length} imóvel{properties.length !== 1 ? 'is' : ''} encontrado{properties.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Properties Grid */}
      {properties.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            {hasActiveFilters ? (
              <>
                <h2 className="text-xl font-semibold mb-2">Nenhum imóvel encontrado</h2>
                <p className="text-muted-foreground mb-4">
                  Tente ajustar os filtros ou termos de busca
                </p>
                <Button variant="outline" onClick={clearAllFilters}>
                  Limpar Filtros
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-2">Nenhum imóvel cadastrado</h2>
                <p className="text-muted-foreground mb-4">
                  Comece adicionando seu primeiro imóvel
                </p>
                <Link to="/dashboard/listings/new">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Imóvel
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {properties.map((property) => (
            <Link 
              key={property.id} 
              to={`/dashboard/listings/${property.id}/edit`}
              className="block"
            >
              <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer">
                <CardContent className="p-0">
                  {/* Image */}
                  <div className="aspect-video relative overflow-hidden rounded-t-lg">
                    {property.featured_image_url ? (
                      <img
                        src={property.featured_image_url}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground text-sm">Sem imagem</span>
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-2 left-2">
                      {getStatusBadge(property.status)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm md:text-base mb-2 line-clamp-2 leading-tight">
                      {property.title}
                    </h3>
                    
                    <div className="text-lg md:text-xl font-bold text-primary mb-2">
                      {formatCurrency(property.price)}
                    </div>

                    {property.city && (
                      <p className="text-xs md:text-sm text-muted-foreground mb-3 truncate">
                        {property.city}, {property.state}
                      </p>
                    )}

                    {/* Visibility Toggle */}
                    <div 
                      className="flex items-center justify-between p-2 bg-muted/30 rounded-lg"
                      onClick={(e) => e.preventDefault()} // Prevent card click when interacting with this area
                    >
                      <div className="flex items-center space-x-1">
                        {property.is_visible_on_storefront !== false ? (
                          <Eye className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
                        ) : (
                          <EyeOff className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                        )}
                        <span className="text-xs md:text-sm font-medium">
                          {property.is_visible_on_storefront !== false ? 'Visível' : 'Oculto'}
                        </span>
                      </div>
                      
                      <Switch
                        checked={property.is_visible_on_storefront !== false}
                        onCheckedChange={(checked) => {
                          // Create a synthetic event to pass to toggleVisibility
                          const syntheticEvent = {
                            preventDefault: () => {},
                            stopPropagation: () => {}
                          } as React.MouseEvent;
                          toggleVisibility(syntheticEvent, property.id, property.is_visible_on_storefront !== false);
                        }}
                        onClick={(e) => toggleVisibility(e, property.id, property.is_visible_on_storefront !== false)}
                        disabled={updatingVisibility === property.id}
                        className="scale-75 md:scale-100"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}